import { FC, useEffect, useRef, useState } from 'react';

import { DataProvider, Input, StaticDataFetcherItem, Suggest } from '@hh.ru/magritte-ui';
import { BarsOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import { useCompositeSelectionContext } from 'components/common/CompositeSelection/contexts';

import { useSelector } from 'store/utils';

interface ErirClassificationInputProps {
    provider: DataProvider<StaticDataFetcherItem>;
}

const TrlKeys = {
    title: 'AdCreate.AdForm.ErirClassification.title',
    notFound: 'AdCreate.AdForm.ErirClassification.input.notFound',
    placeholder: 'AdCreate.AdForm.ErirClassification.input.placeholder',
};

export const ErirClassificationInput: FC<ErirClassificationInputProps> = ({ provider }) => {
    const suggestRef = useRef<HTMLDivElement>(null);
    const bannerErrors = useSelector((state) => state.createBanner.bannerErrors);
    const { setTreeSelectorModalVisibility, collection, value, onChange } = useCompositeSelectionContext();

    const [selectedText, setSelectedText] = useState<string | undefined>(
        value.length > 0 ? collection.getModel(value[0])?.text : ''
    );

    const trl = useTrls();

    const handleOpenModal = () => {
        setTreeSelectorModalVisibility(true);
    };

    const handleChange = (value: string, isValueFromList: boolean) => {
        if (!value || value.trim() === '') {
            onChange((prev) => ({ ...prev, value: [] }));
            setSelectedText('');
            return;
        }
        if (isValueFromList) {
            suggestRef.current?.blur();
            return;
        }
        setSelectedText(value);
    };

    const handleSelectValidator = (value: string) => {
        const foundItem = collection.toList().find((item) => item.id === value);
        if (foundItem) {
            setSelectedText(foundItem.text);
            onChange((prev) => ({ ...prev, value: [foundItem.id] }));
        }
        return true;
    };

    useEffect(() => {
        if (value.length > 0) {
            setSelectedText(collection.getModel(value[0])?.text);
        }
    }, [collection, value]);

    return (
        <Suggest
            ref={suggestRef}
            dataProvider={provider}
            inputValue={selectedText}
            headerTitle={trl(TrlKeys.title)}
            errorPlaceholder={trl(TrlKeys.notFound)}
            trls={{
                resetButton: trl(TrlKeys.notFound),
            }}
            onChange={handleChange}
            onSelectValidator={handleSelectValidator}
            input={{
                component: Input,
                props: {
                    size: 'medium',
                    placeholder: trl(TrlKeys.placeholder),
                    invalid: Boolean(bannerErrors.classificationCodeId),
                    errorMessage: bannerErrors.classificationCodeId,
                    buttonIcon: BarsOutlinedSize24,
                    onButtonClick: handleOpenModal,
                    'data-qa': 'erir-classification-input',
                },
            }}
        />
    );
};
